import { Button, Checkbox, Tag, Tooltip, Typography } from "antd";
import { useState, type ReactElement } from "react";
import { TbLock } from "react-icons/tb";
import type {
  T_AssistantOffer,
  T_AssistantOfferGrade,
  T_AssistantOfferVariables,
  T_GradeKey,
} from "dtos/offer-assistant.dto";
import {
  GRADE_META,
  GRADE_ORDER,
  LEVEL_METER_CLASS,
  LEVEL_TEXT_CLASS,
  PERIOD_WEEKS,
  UNIT,
  affordLevel,
  gradeLevel,
  heldText,
  money,
  plural,
  score,
  scoreFraction,
  shapeText,
  variantLabel,
  type T_StripScale,
} from "utils/offer-assistant.utils";
import { useOfferAssistantApi } from "hooks/useOfferAssistant";

const { Text } = Typography;

// ─── Constants ────────────────────────────────────────────────────────────────

const TICK_LIMIT = 40;
// Tailwind can't express these patterns as utilities — kept as inline backgrounds.
const DEFERRAL_HATCH = "repeating-linear-gradient(135deg, #C4C7CF 0 1px, transparent 1px 4px)";
const BAND_STRIPES = "repeating-linear-gradient(90deg, #A08BFC 0 1px, transparent 1px 3px)";

/** Compact tag sizing used in tables, compare headers and pick summaries. */
export const XS_TAG = "!m-0 !px-1.5 !text-[11px] !leading-[17px]";

// ─── Payment strip ────────────────────────────────────────────────────────────

/**
 * Payment shape strip: hatched deferral → violet upfront block (height = amount)
 * → one tick per periodic payment (height = amount), all on a shared week axis.
 * Heights use a square-root scale so small payments stay visible next to big lump sums.
 */
export function PayStrip({
  v,
  scale,
  height = 20,
  muted,
  tooltips = true,
}: {
  v: T_AssistantOfferVariables;
  /** Shared across every strip in the same block */
  scale: T_StripScale;
  height?: number;
  muted?: boolean;
  tooltips?: boolean;
}) {
  const x = (weeks: number) => Math.min(100, (weeks / scale.weeks) * 100);
  const y = (amount: number) => Math.round(Math.max(0.22, Math.sqrt(amount / scale.amount)) * 100);

  const def = v.deferral_weeks || 0;
  const hasUp = v.upfront_amount > 0;
  const runStart = def + (hasUp ? 1 : 0);
  const runWeeks = v.total_period * PERIOD_WEEKS[v.payment_frequency];
  const band = v.total_period > TICK_LIMIT;
  const tickH = `${y(v.periodic_amount)}%`;

  const tip = (title: string, node: ReactElement) =>
    tooltips ? (
      <Tooltip title={title} mouseEnterDelay={0.05}>
        {node}
      </Tooltip>
    ) : (
      node
    );

  return (
    <div
      role="img"
      aria-label={shapeText(v)}
      className={`relative w-full border-0 border-b border-solid border-gray-200 ${muted ? "opacity-50" : ""}`}
      style={{ height }}
    >
      {def > 0 &&
        tip(
          `Deferral: ${plural(def, "week")}`,
          <div
            className="absolute bottom-0 left-0 h-full rounded-sm bg-gray-100"
            style={{ width: `${x(def)}%`, backgroundImage: DEFERRAL_HATCH }}
          />,
        )}
      {hasUp &&
        tip(
          `Upfront: ${money(v.upfront_amount)}`,
          <div
            className="absolute bottom-0 min-w-[5px] rounded-t-sm bg-violet-500"
            style={{ left: `${x(def)}%`, width: `${x(1)}%`, height: `${y(v.upfront_amount)}%` }}
          />,
        )}
      {v.total_period > 0 &&
        tip(
          `${v.total_period} ${v.payment_frequency} payments of ${money(v.periodic_amount)}`,
          <div
            className="absolute inset-y-0"
            style={{ left: `${x(runStart)}%`, width: `${x(runStart + runWeeks) - x(runStart)}%` }}
          >
            {band ? (
              <div
                className="absolute inset-x-0 bottom-0 rounded-t-[1px] bg-violet-200"
                style={{ height: tickH, backgroundImage: BAND_STRIPES }}
              />
            ) : (
              Array.from({ length: v.total_period }, (_, i) => (
                <span
                  key={i}
                  className="pointer-events-none absolute bottom-0 -ml-px w-0.5 rounded-t-[1px] bg-violet-300"
                  style={{ left: `${((i + 0.5) / v.total_period) * 100}%`, height: tickH }}
                />
              ))
            )}
          </div>,
        )}
    </div>
  );
}

// ─── Tags / meters / grades ───────────────────────────────────────────────────

/** Renders the ★ Recommended and ⚖ Balanced tags based on an offer's flags. */
export function RecBalTags({ offer, xs }: { offer: T_AssistantOffer; xs?: boolean }) {
  const cls = xs ? XS_TAG : "!m-0";
  return (
    <>
      {offer.recommended && (
        <Tag color="gold" bordered={false} className={cls}>
          ★ Recommended
        </Tag>
      )}
      {offer.balanced && (
        <Tag color="cyan" bordered={false} className={cls}>
          ⚖ Balanced
        </Tag>
      )}
    </>
  );
}

function Meter({ pct, colorClass, thin }: { pct: number; colorClass: string; thin?: boolean }) {
  return (
    <div className={`overflow-hidden rounded-sm bg-gray-100 ${thin ? "col-span-full h-[3px]" : "h-1"}`} aria-hidden>
      <div className={`h-full rounded-sm ${colorClass}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

function Grades({ grade, owed, emphasis }: { grade: T_AssistantOfferGrade; owed: number; emphasis?: T_GradeKey }) {
  const order = emphasis ? [emphasis, ...GRADE_ORDER.filter((k) => k !== emphasis)] : GRADE_ORDER;
  return (
    <div className={`grid gap-3.5 ${emphasis ? "grid-cols-[1.25fr_1fr_1fr]" : "grid-cols-3"}`}>
      {order.map((k) => {
        const m = GRADE_META[k];
        const val = grade[k];
        const lvl = gradeLevel(k, val);
        const pct = k === "economic_worth" ? (owed ? (val / owed) * 100 : 0) : scoreFraction(val) * 100;
        const size = !emphasis ? "text-base" : emphasis === k ? "text-xl" : "text-sm";
        return (
          <div key={k} className="flex min-w-0 flex-col gap-1">
            <Tooltip title={m.tooltip}>
              <Text type="secondary" className="cursor-help truncate text-[11.5px]" tabIndex={0}>
                {m.label}
              </Text>
            </Tooltip>
            <Text className={`font-semibold leading-tight tabular-nums ${size} ${LEVEL_TEXT_CLASS[lvl]}`}>
              {m.format(val)}
            </Text>
            <Meter pct={pct} colorClass={LEVEL_METER_CLASS[lvl]} />
          </div>
        );
      })}
    </div>
  );
}

// ─── Expanded details ─────────────────────────────────────────────────────────

function ExpandedDetails({ offer }: { offer: T_AssistantOffer }) {
  const [open, setOpen] = useState(true);
  const v = offer.variables;
  const g = offer.grade;
  const b = g.affordability_breakdown;

  const rows: [string, string][] = [
    ["Upfront payment", v.upfront_amount ? money(v.upfront_amount) : "None"],
    ["Payment amount", `${money(v.periodic_amount)} per ${UNIT[v.payment_frequency]}`],
    ["Number of payments", String(v.total_period)],
    ["Deferral", v.deferral_weeks ? plural(v.deferral_weeks, "week") : "None"],
    ["Total paid (nominal)", money(g.nominal_total)],
    ["Value to creditor (present value)", money(g.economic_worth)],
  ];
  const subs: [string, number][] = [
    ["Cash on hand vs upfront", b.cash_vs_upfront],
    ["Weekly cashflow vs payment", b.cashflow_vs_payment],
    ["Breathing room from deferral", b.deferral_room],
  ];

  return (
    <div className="flex flex-col gap-3 border-0 border-t border-solid border-gray-100 pt-3">
      {offer.held && (
        <Text type="secondary" className="inline-flex items-center gap-1 text-xs">
          <TbLock /> {heldText(offer.held.anchor, offer.held.value)}
        </Text>
      )}

      <dl className="m-0 flex flex-col gap-2">
        {rows.map(([label, val]) => (
          <div key={label} className="flex justify-between gap-3">
            <dt>
              <Text type="secondary" className="text-[12.5px]">
                {label}
              </Text>
            </dt>
            <dd className="m-0 text-right">
              <Text className="text-[12.5px] font-medium tabular-nums">{val}</Text>
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-col gap-2.5 rounded-lg border border-solid border-gray-100 bg-gray-50 px-3 py-2.5">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-left"
        >
          <span className="w-2.5 text-[10px] text-gray-400">{open ? "▾" : "▸"}</span>
          <Text className="flex-1 text-[12.5px] font-semibold !text-gray-700">Affordability breakdown</Text>
          <Text
            className={`text-[12.5px] font-semibold tabular-nums ${LEVEL_TEXT_CLASS[affordLevel(g.client_utility)]}`}
          >
            {score(g.client_utility)}
          </Text>
        </button>

        {open && (
          <div className="flex flex-col gap-2">
            {subs.map(([label, x]) => {
              const lvl = affordLevel(x);
              return (
                <div key={label} className="grid grid-cols-[minmax(0,1fr)_44px] items-center gap-x-2.5 gap-y-1">
                  <Text className="text-xs !text-gray-600">{label}</Text>
                  <Text className={`text-right text-xs font-semibold tabular-nums ${LEVEL_TEXT_CLASS[lvl]}`}>
                    {score(x)}
                  </Text>
                  <Meter pct={scoreFraction(x) * 100} colorClass={LEVEL_METER_CLASS[lvl]} thin />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function OfferCard({
  offer,
  scale,
  density = "compact",
  badge,
  emphasis,
}: {
  offer: T_AssistantOffer;
  scale: T_StripScale;
  density?: "compact" | "expanded";
  badge?: string;
  /** Ranked lists: this grade goes first and larger */
  emphasis?: T_GradeKey;
}) {
  const { send, insert, isSelected, toggleSelect, owed } = useOfferAssistantApi();
  const isVariant = offer.kind === "variant";
  const isWhatIf = offer.kind === "whatif";
  const expanded = density === "expanded";
  const selected = !isWhatIf && isSelected(offer.ref);
  const title = isWhatIf ? "Your offer" : isVariant ? variantLabel(offer.ref) : offer.ref;

  const stateCls = selected
    ? "border-violet-500 bg-violet-50/40"
    : isWhatIf
      ? "border-dashed border-gray-400"
      : "border-gray-200 hover:border-violet-300";

  return (
    <article
      aria-label={offer.ref}
      className={`flex flex-col gap-3 rounded-lg border border-solid bg-white px-3.5 pb-2 pt-3.5 transition-colors ${stateCls}`}
    >
      <header className="flex min-h-[22px] flex-wrap items-center gap-2">
        <Text strong className="whitespace-nowrap text-[13.5px]">
          {title}
        </Text>
        <RecBalTags offer={offer} />
        {isVariant && (
          <Tag bordered={false} className="!m-0">
            Variant of {offer.parent_ref}
          </Tag>
        )}
        {isWhatIf && <Tag className="!m-0 !border-dashed !bg-white">What-if, not saved</Tag>}
        <span className="flex-1" />
        {badge && <Text className="whitespace-nowrap text-[11.5px] !text-gray-600">{badge}</Text>}
      </header>

      <div className="flex flex-col gap-2">
        <PayStrip v={offer.variables} scale={scale} />
        <Text className="text-[12.5px] leading-[18px] !text-gray-600">{shapeText(offer.variables)}</Text>
      </div>

      <Grades grade={offer.grade} owed={owed} emphasis={emphasis} />

      {expanded && <ExpandedDetails offer={offer} />}

      <footer className="-mx-1.5 flex items-center gap-0.5">
        {isWhatIf ? (
          <Button
            type="link"
            size="small"
            onClick={() => send(`Make variants of this offer: ${shapeText(offer.variables)}`)}
          >
            Make variants of this
          </Button>
        ) : (
          <>
            {!isVariant && (
              <Button type="link" size="small" onClick={() => send(`Make variants of ${offer.ref}`)}>
                Variants
              </Button>
            )}
            {expanded ? (
              <Button type="link" size="small" onClick={() => insert(`Compare ${offer.ref} with @`)}>
                Compare with…
              </Button>
            ) : (
              <Button type="link" size="small" onClick={() => send(`Show me ${offer.ref}`)}>
                Details
              </Button>
            )}
            <span className="flex-1" />
            <Checkbox
              className="rounded-md px-2 py-1 text-[12.5px] hover:bg-gray-100"
              checked={selected}
              onChange={() => toggleSelect(offer.ref)}
            >
              {selected ? "Selected" : "Select"}
            </Checkbox>
          </>
        )}
      </footer>
    </article>
  );
}
